import { memo } from 'react';
import { useTheme } from '@simplywallst/ui-core';
import BarChartVertical from '@components/BarChartVertical';

interface Props {
  companyName: string;
  marketName: string;
  industryName: string;
  netIncomeGrowth1Y: number | null;
  netIncomeGrowth5Y: number | null;
  averageMarketGrowth1Y: number | null;
  averageMarketGrowth5Y: number | null;
  averageIndustryGrowth1Y: number | null;
  averageIndustryGrowth5Y: number | null;
  netIncomeGrowth5YFormatted: string;
  averageIndustryGrowth5YFormatted: string;
  averageMarketGrowth5YFormatted: string;
  netIncomeGrowth1YFormatted: string;
  averageIndustryGrowth1YFormatted: string;
  averageMarketGrowth1YFormatted: string;
  labels?: {
    chartPast5Years: string;
    chartPast1Year: string;
    keyCompany: string;
    keyIndustry: string;
    keyMarket: string;
  };
}

const charts = [
  'past 5 years annual earnings growth',
  'last 1 year earnings growth',
];

const keys = ['Company', 'Industry', 'Market'];

const PastGrowth = ({
  companyName,
  industryName,
  marketName,
  netIncomeGrowth1Y,
  netIncomeGrowth5Y,
  averageMarketGrowth1Y,
  averageMarketGrowth5Y,
  averageIndustryGrowth1Y,
  averageIndustryGrowth5Y,
  netIncomeGrowth5YFormatted,
  averageIndustryGrowth5YFormatted,
  averageMarketGrowth5YFormatted,
  netIncomeGrowth1YFormatted,
  averageIndustryGrowth1YFormatted,
  averageMarketGrowth1YFormatted,
  labels = {
    chartPast5Years: 'past 5 years annual earnings growth',
    chartPast1Year: 'last 1 year earnings growth',
    keyCompany: 'Company',
    keyIndustry: 'Industry',
    keyMarket: 'Market',
  },
}: Props) => {
  const theme = useTheme();

  const entities = {
    [charts[0]]: {
      [keys[0]]: netIncomeGrowth5Y,
      [keys[1]]: averageIndustryGrowth5Y,
      [keys[2]]: averageMarketGrowth5Y,
    },
    [charts[1]]: {
      [keys[0]]: netIncomeGrowth1Y,
      [keys[1]]: averageIndustryGrowth1Y,
      [keys[2]]: averageMarketGrowth1Y,
    },
  };

  const entitiesFormatted = {
    [charts[0]]: {
      [keys[0]]: netIncomeGrowth5YFormatted,
      [keys[1]]: averageIndustryGrowth5YFormatted,
      [keys[2]]: averageMarketGrowth5YFormatted,
    },
    [charts[1]]: {
      [keys[0]]: netIncomeGrowth1YFormatted,
      [keys[1]]: averageIndustryGrowth1YFormatted,
      [keys[2]]: averageMarketGrowth1YFormatted,
    },
  };

  const entityTooltips = {
    [charts[0]]: {
      [keys[0]]: companyName,
      [keys[1]]: industryName,
      [keys[2]]: marketName,
    },
    [charts[1]]: {
      [keys[0]]: companyName,
      [keys[1]]: industryName,
      [keys[2]]: marketName,
    },
  };

  return (
    <BarChartVertical
      charts={charts}
      chartLabels={[labels.chartPast5Years, labels.chartPast1Year]}
      entities={entities}
      entitiesFormatted={entitiesFormatted}
      entityTooltips={entityTooltips}
      keys={keys}
      keyLabels={[labels.keyCompany, labels.keyIndustry, labels.keyMarket]}
      colors={theme.chart.barChart.pastGrowth.colors}
      titleColors={theme.chart.barChart.pastGrowth.titleColors}
      height={250}
    />
  );
};

export default memo(PastGrowth);
